import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Filter, RefreshCcw } from 'lucide-react';
import {
  BosDurum,
  Rozet,
  TarihAraligiSecici,
  VeriTablo,
  tarihIso,
  type ColumnDef,
  type RozetTonu,
  type TarihAraligi,
} from '@emanetx/ui';
import { iadeleriListele, type Iade, type IadeDurumu, type IadeSebebi } from '@emanetx/sdk';
import { istemci } from '../istemci.js';

const DURUM_TONU: Record<IadeDurumu, RozetTonu> = {
  BEKLEMEDE: 'turuncu',
  TAMAMLANDI: 'yesil',
  REDDEDILDI: 'kirmizi',
};

const DURUM_ETIKET: Record<IadeDurumu, string> = {
  BEKLEMEDE: 'Beklemede',
  TAMAMLANDI: 'Tamamlandı',
  REDDEDILDI: 'Reddedildi',
};

const SEBEP_ETIKET: Record<IadeSebebi, string> = {
  TEKNIK_ARIZA: 'Teknik arıza',
  MUSTERI_TALEBI: 'Müşteri talebi',
  ERKEN_IPTAL: 'Erken iptal',
  ADMIN_DUZELTME: 'Admin düzeltme',
  DIGER: 'Diğer',
};

function sonOtuzGun(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setDate(s.getDate() - 29);
  s.setHours(0, 0, 0, 0);
  return { baslangic: s, bitis: e };
}

export function SayfaIadeler() {
  const [sayfa, setSayfa] = useState(1);
  const [durum, setDurum] = useState<IadeDurumu | ''>('');
  const [sebep, setSebep] = useState<IadeSebebi | ''>('');
  const [aralik, setAralik] = useState<TarihAraligi>(sonOtuzGun);

  const sorgu = {
    sayfa,
    boyut: 50,
    ...(durum && { durum: durum as IadeDurumu }),
    ...(sebep && { sebep: sebep as IadeSebebi }),
    ...(tarihIso(aralik.baslangic) && { baslangicTen: tarihIso(aralik.baslangic) }),
    ...(tarihIso(aralik.bitis) && { baslangicAKadar: tarihIso(aralik.bitis) }),
  };

  const liste = useQuery({
    queryKey: ['iadeler', 'liste', sorgu],
    queryFn: () => iadeleriListele(istemci, sorgu),
    placeholderData: (onceki) => onceki,
  });

  const kolonlar = useMemo<ColumnDef<Iade, unknown>[]>(
    () => [
      {
        accessorKey: 'olusturmaTarihi',
        header: 'Zaman',
        meta: { etiket: 'Zaman' },
        cell: ({ row }) => (
          <span className="text-fume-500 font-mono text-xs">
            {new Date(row.original.olusturmaTarihi).toLocaleString('tr-TR', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ),
      },
      {
        accessorKey: 'kiralamaKod',
        header: 'Kiralama',
        meta: { etiket: 'Kiralama' },
        cell: ({ row }) => (
          <span className="text-marka-700 dark:text-marka-400 font-mono text-xs">
            {row.original.kiralamaKod ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'miktar',
        header: 'Tutar',
        meta: { etiket: 'Tutar' },
        cell: ({ row }) => {
          const sembol =
            row.original.paraBirimi === 'TRY' ? '₺' : row.original.paraBirimi === 'EUR' ? '€' : '$';
          return (
            <span className="font-mono font-semibold">
              {sembol}
              {row.original.miktar}
            </span>
          );
        },
        sortingFn: (a, b) => Number(a.original.miktar) - Number(b.original.miktar),
      },
      {
        accessorKey: 'sebep',
        header: 'Sebep',
        meta: { etiket: 'Sebep' },
        cell: ({ row }) => <span className="text-xs">{SEBEP_ETIKET[row.original.sebep]}</span>,
      },
      {
        accessorKey: 'durum',
        header: 'Durum',
        meta: { etiket: 'Durum' },
        cell: ({ row }) => (
          <Rozet ton={DURUM_TONU[row.original.durum]} nokta>
            {DURUM_ETIKET[row.original.durum]}
          </Rozet>
        ),
      },
      {
        accessorKey: 'yapanAdSoyad',
        header: 'Yapan',
        meta: { etiket: 'Yapan' },
        cell: ({ row }) => (
          <span className="text-fume-500 text-xs">{row.original.yapanAdSoyad ?? '—'}</span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">İadeler</h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          Tüm iade kayıtları — sebep, tutar, durum.
        </p>
      </header>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={50}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        bosDurum={
          <BosDurum
            ikon={RefreshCcw}
            baslik="Bu tarih aralığında iade yok"
            aciklama="Tarih aralığını veya filtreyi değiştirin."
          />
        }
        ustAksiyonlar={
          <>
            <TarihAraligiSecici
              deger={aralik}
              onDegisim={(a) => {
                setAralik(a);
                setSayfa(1);
              }}
              etiketYok
              className="h-10 text-xs"
            />
            <Filter size={13} className="text-fume-400" />
            <select
              value={durum}
              onChange={(e) => {
                setDurum(e.target.value as IadeDurumu | '');
                setSayfa(1);
              }}
              className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs focus:outline-none focus:ring-2"
            >
              <option value="">Tüm durumlar</option>
              <option value="BEKLEMEDE">Beklemede</option>
              <option value="TAMAMLANDI">Tamamlandı</option>
              <option value="REDDEDILDI">Reddedildi</option>
            </select>
            <select
              value={sebep}
              onChange={(e) => {
                setSebep(e.target.value as IadeSebebi | '');
                setSayfa(1);
              }}
              className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs focus:outline-none focus:ring-2"
            >
              <option value="">Tüm sebepler</option>
              <option value="TEKNIK_ARIZA">Teknik arıza</option>
              <option value="MUSTERI_TALEBI">Müşteri talebi</option>
              <option value="ERKEN_IPTAL">Erken iptal</option>
              <option value="ADMIN_DUZELTME">Admin düzeltme</option>
              <option value="DIGER">Diğer</option>
            </select>
          </>
        }
      />
    </div>
  );
}
